import { ROLE_LABELS, type Role } from "@nh/shared";
import type { BadgeVariant } from "@/shared/ui/core";

/**
 * Lo que la Bitácora pinta igual en sus tres pestañas: la hora, el color
 * de un estado HTTP, el rol y el JSON de la petición.
 */

/**
 * Fecha y hora EXACTAS, con segundos, en la hora de la Ciudad de México:
 * en una bitácora dos renglones del mismo minuto se distinguen por el
 * segundo, y el servidor guarda UTC.
 */
const DATETIME = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "medium",
  timeStyle: "medium",
  timeZone: "America/Mexico_City",
});

export function formatAuditDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return DATETIME.format(date);
}

/** Verde lo que salió bien, ámbar lo que se rechazó, rojo lo que se rompió. */
export function statusVariant(status: number): BadgeVariant {
  if (status >= 500) return "error";
  if (status >= 400) return "warning";
  if (status >= 200 && status < 300) return "success";
  return "neutral";
}

/** El rol con su nombre en español; uno desconocido se enseña tal cual. */
export function roleLabel(role: string | undefined): string | undefined {
  if (!role) return undefined;
  return (ROLE_LABELS as Record<string, string>)[role as Role] ?? role;
}

/**
 * El cuerpo de una petición, legible. Es JSON arbitrario (ya filtrado en
 * el servidor); si por algo no se puede serializar, se enseña como texto.
 */
export function prettyJson(value: unknown): string {
  if (value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/*
 * ── Del día del selector al instante del contrato ────────────────────
 * El `<input type="date">` da `YYYY-MM-DD` y el contrato pide un ISO en
 * UTC. El día se lee en la hora de la Ciudad de México (UTC−6 todo el
 * año desde que se abolió el horario de verano en 2022): «desde el 1 de
 * octubre» es desde la medianoche de aquí, no la de Greenwich.
 */
const MX_OFFSET = "-06:00";

export function dayStartIso(day: string): string | undefined {
  if (!day) return undefined;
  const date = new Date(`${day}T00:00:00.000${MX_OFFSET}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function dayEndIso(day: string): string | undefined {
  if (!day) return undefined;
  const date = new Date(`${day}T23:59:59.999${MX_OFFSET}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
